import React, { useState } from "react";
import { useRecoilState } from "recoil";
import { Form, Formik } from "formik";

import {
  BaseDropdown,
  BaseInput,
  FileUpload,
  LargeButton,
  OverlayLoader,
} from "components/core";
import useNavigateInsuranceSetup from "hooks/useNavigateInsuranceSetup";
import { completedInsuranceProductSetup } from "state/planSetup";
import { gmcFormScheme, gmcInitialValues } from "./schemas";
import { uploadFile } from "utlis/utils";
import http from "services/httpService";
import { Divider } from "@mui/material";
import BaseCustomizeInput from "components/GhiPlanSetup";
import MaternityCoverge from "components/GhiPlanSetup/MaternityCoverage";
import DiseaseWiseLimit from "components/GhiPlanSetup/DiseaseWiseLimit";
import CoPay from "./CoPay";
import OpdLimit from "./OpdLimit";
import CorporateBuffer from "./CorporateBuffer";
import WellnessBenefits from "./WellnessBenefits";
import GmcPlanSetup from "./GmcPlanSetup";

const GmcForm = ({ details }) => {
  const [isLoading, setIsLoading] = useState(false);
  // custom hook to handle insurance product navigation
  const { navigateInsurancePlan } = useNavigateInsuranceSetup();
  // storing file
  const [file, setFile] = useState();
  // storing file error state
  const [fileError, setFileError] = useState();
  // global completed setup
  const [completedSetupIndices, setCompletedSetupIndices] = useRecoilState(
    completedInsuranceProductSetup
  );
  const [selectedAttributes, setAttributes] = useState(); // selected attributes list
  // handle form submission
  const handleSubmit = async (values, { setSubmitting, setTouched }) => {
    // exiting if no file
    // if (!file) return;
    // setting touched to true to display errors if errors persist
    setTouched(
      Object.keys(values).reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
      }, {})
    );
    setIsLoading(true);
    localStorage.setItem('GmcPlanSetup', JSON.stringify(values))
    localStorage.setItem('gmc_done','RATECAHRT')
    // const returnValue = await GmcPlanSetup(values, setIsLoading)
    // if(returnValue === false){
    //   return
    // }else(
    //   localStorage.setItem('gmc_done','RATECAHRT')
    // )
    // marking gmc form as completed
    const marked_products = localStorage.getItem("marked_products");
    if (!marked_products) {
      localStorage.setItem("marked_products", JSON.stringify([0]));
    } else {
      const parsed = JSON.parse(marked_products);
      localStorage.setItem("marked_products", JSON.stringify([...parsed, 0]));
    }
    setCompletedSetupIndices([...new Set(completedSetupIndices).add(0)]);

    // navigating to next page
    navigateInsurancePlan();
  };

  const uploadRateChart = async (fileId) => {
    const rateChartData = new FormData();

    rateChartData.append("document_id", fileId);
    rateChartData.append("gmc_type", "annual");

    const options = {
      "Content-Type": "multipart/form-data",
    };

    const { data } = await http.post(
      "/insurance/setup_gmc_premiums/",
      rateChartData,
      options
    );

    return data.premium_id;
  };

  // save gmc setup
  const setupGmc = async (
    premium_id,
    {
      insurer: insurer_name,
      // rewards_alloted: rewards,
      maternity: maternity_limit,
      sum_insured,
      benefit_type,
      room_type,
      // commission,
    }
  ) => {
    // getting business user id
    const business_user_id = localStorage.getItem("id");
    // getting userplan_ids -> it would be different for different plans
    const userplan_ids = localStorage.getItem("userplan_ids");
    try {
      let { data } = await http.post("/business/setupplan/", {
        business_type: maternity_limit, // type is GMC
        business_user_id,
        // rewards,
        insurer_name,
        // premium_id, // file id of the document uploaded
        sum_insured,
        // commission,
        benefit_type,
        room_limit: room_type,
        // maternity_limit, // GMC_M or GMC
      });

      if (!userplan_ids) {
        // creating a new local storage for userplan_ids and storing the strigified value
        localStorage.setItem(
          "userplan_ids",
          JSON.stringify([data.userplan_id])
        );
      } else {
        // adding the new userplan_id if it already exist
        const parsed = JSON.parse(userplan_ids);
        localStorage.setItem(
          "userplan_ids",
          JSON.stringify([...parsed, data.userplan_id])
        );
      }
      localStorage.setItem('gmc_business_type', maternity_limit)
    } catch (err) {
      console.log({ err });
      alert("Some error has occured!");
      return;
    } finally {
      setIsLoading(false);
    }
  };

  // handling file error
  const handleError = () => {
    if (!file) setFileError(true);
  };

  const getDropdownOptions = (selectedInsurer, field) => {
    let options = [];
    
    // returning empty array if insurer or materity is not selected
    if (!selectedInsurer) {
      return options;
    } else {
      // setting options of the given field based on the selected insurer and maternity
      options = getOptions(details[field][selectedInsurer]);
    }

    return options;
  };

  return (
    <>
      {isLoading ? (
        <OverlayLoader />
      ) : (
        <Formik
          enableReinitialize={true}
          initialValues={gmcInitialValues}
          validationSchema={gmcFormScheme}
          onSubmit={handleSubmit}
        >
          {(formikProps) => (
            <Form>
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Insurer"
                  options={getOptions(details["insurers"])}
                  formikProps={{ ...formikProps, name: "insurer" }}
                />
              </div>
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Select Dependent Type"
                  options={getOptions(details["dependent_type"])}
                  formikProps={{ ...formikProps, name: "dependent_type" }}
                />
              </div>
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Sum Insured"
                  options={getOptions(details["sum_insured"])}
                  formikProps={{ ...formikProps, name: "sum_insured" }}
                />
              </div>
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Benefit Type"
                  options={getDropdownOptions(formikProps.values.insurer, 'benefit_type')}
                  formikProps={{ ...formikProps, name: "benefit_type" }}
                />
              </div>
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Room Limit"
                  options={getOptions(details["room_limit"])}
                  formikProps={{ ...formikProps, name: "room_limit" }}
                />
              </div>
              {formikProps.values.room_limit ==
                "Customise Limit (by metro & Non metro)" && (
                <BaseCustomizeInput
                  label={"Room Limit"}
                  formikProps={formikProps}
                />
              )}
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Maternity"
                  options={getOptions(details["maternity_coverage"])}
                  formikProps={{ ...formikProps, name: "maternity_coverage" }}
                />
              </div>
              {formikProps.values.maternity_coverage == true && (
                <MaternityCoverge
                  label={"Room Limit"}
                  formikProps={formikProps}
                />
              )}
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Utilization Limit for Parents"
                  options={getOptions(details["utilization_limit_for_parents"])}
                  formikProps={{
                    ...formikProps,
                    name: "utilization_limit_for_parents",
                  }}
                />
              </div>
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Disease-wise Limit"
                  options={getOptions(details["disease_wise_limit"])}
                  formikProps={{ ...formikProps, name: "disease_wise_limit" }}
                />
              </div>
              {formikProps.values.disease_wise_limit == "Yes" && (
                <DiseaseWiseLimit
                  formikProps={formikProps}
                  dropDownValues={getOptions(details["disease_names"])}
                />
              )}
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Co-pay"
                  options={getOptions(details["co_pay"])}
                  formikProps={{ ...formikProps, name: "co_pay" }}
                />
              </div>
              {formikProps.values.co_pay &&
                formikProps.values.co_pay !== "No" && (
                  <CoPay formikProps={formikProps} />
                )}
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Waiting Period"
                  options={getOptions(details["waiting_period"])}
                  formikProps={{ ...formikProps, name: "waiting_period" }}
                />
              </div>
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="OPD"
                  options={getOptions(details["opd_included"])}
                  formikProps={{ ...formikProps, name: "opd_included" }}
                />
              </div>
              {formikProps.values.opd_included == true && (
                <OpdLimit formikProps={formikProps} />
              )}
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Wellness Benefit Options"
                  options={getOptions(details["wellness_benefits_options"])}
                  formikProps={{
                    ...formikProps,
                    name: "wellness_benefits_options",
                  }}
                />
              </div>
              {formikProps.values.wellness_benefits_options == "Yes" && (
                <WellnessBenefits
                  displayVlaues={details["wellness_benefits"]}
                  onChange={setAttributes}
                  formikProps={{
                    ...formikProps,
                    name: "wellness_benefits",
                  }}
                />
              )}
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Corporate Buffer"
                  options={getOptions(details["corporate_buffer"])}
                  formikProps={{ ...formikProps, name: "corporate_buffer" }}
                />
              </div>
              {formikProps.values.corporate_buffer == true && (
                <CorporateBuffer formikProps={formikProps} />
              )}
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseDropdown
                  fontSize="14"
                  label="Premium Contribution"
                  options={getOptions(details["premium_contribution"])}
                  formikProps={{ ...formikProps, name: "premium_contribution" }}
                />
              </div>
              <div style={{ marginBottom: "2.8rem" }}>
                <BaseInput
                  label="Enter Ambulance Limit"
                  type="text"
                  // formatToRupee
                  formikProps={{ ...formikProps, name: "ambulance_limit" }}
                />
              </div>
              {/* <div style={{ margin: "4rem 0rem" }}>
                <Divider />
              </div>
              <FileUpload
                label="Upload Rate Chart"
                text="Upload Excel"
                onSelect={setFile}
                showError={fileError}
                bottom="-5rem"
              /> */}
              <div style={{ marginTop: "4rem" }}>
                <LargeButton
                  type="submit"
                  label="Save & Next"
                  marginTop="2rem"
                  onClick={handleError}
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

const getOptions = (details) => {
  if (!details) return [];
  return details.map(({ display_name, value }) => ({
    label: display_name,
    value,
  }));
};

export default GmcForm;
